<template>
  <div class="relative min-w-0 w-full mb-8">
    <button
      type="button"
      class="absolute -left-5 top-1/2 z-[1] mx-2.5 flex h-[30px] w-[30px] -translate-y-1/2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-black hover:enabled:text-[#090f207f] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-25"
      v-show="hasTabsOverflow"
      :disabled="!canScrollTabsPrev"
      aria-label="Previous category"
      @click.stop.prevent="scrollTabsBy(-1)"
    >
      <svg class="h-7 w-7 fill-current" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
      </svg>
    </button>

    <div
      ref="tabsRef"
      :class="hasTabsOverflow ? 'mx-5 w-[calc(100%-2.5rem)]' : 'mx-0 w-full'"
      class="relative flex min-w-0 items-center overflow-x-auto scroll-smooth pt-1 pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory scroll-px-2"
      role="tablist"
      @scroll="updateTabsScrollState"
    >
      <button
        v-for="categoryList in categoryLists"
        :key="categoryList.category_id"
        type="button"
        role="tab"
        :aria-selected="String(categoryList.category_id) === activeId"
        :class="{ 'bg-primary-light': String(categoryList.category_id) === activeId }"
        class="carousel__item snap-start shrink-0 whitespace-nowrap rounded border hover:bg-primary-light px-5 mx-2 py-2 border-turquoise-medium cursor-pointer text-blog-13-sm md:text-blog-16 font-semibold"
        :id="categoryList.category_id"
        @click="activeTab(categoryList.category_id, $event)"
      >
        {{ categoryList.name }}
      </button>
    </div>

    <button
      type="button"
      class="absolute -right-5 top-1/2 z-[1] mx-2.5 flex h-[30px] w-[30px] -translate-y-1/2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-black hover:enabled:text-[#090f207f] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-25"
      v-show="hasTabsOverflow"
      :disabled="!canScrollTabsNext"
      aria-label="Next category"
      @click.stop.prevent="scrollTabsBy(1)"
    >
      <svg class="h-7 w-7 fill-current" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
      </svg>
    </button>
  </div>
</template>

<script setup>
const emit = defineEmits(["emitCategoryId"]);
const props = defineProps({
  categoryLists: {
    type: Array,
  },
});

const tabsRef = ref(null);
const activeId = shallowRef("all");
const canScrollTabsPrev = ref(false);
const canScrollTabsNext = ref(false);
const hasTabsOverflow = computed(() => canScrollTabsPrev.value || canScrollTabsNext.value);
let tabsObserver = null;

const updateTabsScrollState = () => {
  const tabs = tabsRef.value;
  if (!tabs) {
    canScrollTabsPrev.value = false;
    canScrollTabsNext.value = false;
    return;
  }
  const maxScroll = tabs.scrollWidth - tabs.clientWidth;
  canScrollTabsPrev.value = tabs.scrollLeft > 2;
  canScrollTabsNext.value = maxScroll > 2 && tabs.scrollLeft < maxScroll - 2;
};

const observeTabs = () => {
  const tabs = tabsRef.value;
  tabsObserver?.disconnect();
  if (!tabs || typeof ResizeObserver === "undefined") {
    return;
  }
  tabsObserver = new ResizeObserver(() => updateTabsScrollState());
  tabsObserver.observe(tabs);
  [...tabs.children].forEach((child) => tabsObserver.observe(child));
};

const refreshTabsLayout = async () => {
  if (!import.meta.client) {
    return;
  }
  await nextTick();
  observeTabs();
  updateTabsScrollState();
  requestAnimationFrame(updateTabsScrollState);
};

const scrollTabsBy = (direction) => {
  const tabs = tabsRef.value;
  if (!tabs) {
    return;
  }
  // Slide by roughly one chip width (matches HowItWorks tab strip).
  const amount = Math.max(tabs.clientWidth * 0.65, 160);
  tabs.scrollBy({ left: direction * amount, behavior: "smooth" });
};

const scrollTabIntoView = (tab) => {
  const tabs = tabsRef.value;
  if (!tabs || !tab) {
    return;
  }
  const target = tab.offsetLeft - (tabs.clientWidth - tab.offsetWidth) / 2;
  tabs.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
};

function activeTab(id, event) {
  activeId.value = String(id);
  emit("emitCategoryId", String(id));
  scrollTabIntoView(event.currentTarget);
}

watch(
  () => props.categoryLists?.map((category) => category.category_id),
  () => refreshTabsLayout(),
  { flush: "post" }
);

onMounted(() => {
  refreshTabsLayout();
  window.addEventListener("resize", updateTabsScrollState);
});

onBeforeUnmount(() => {
  tabsObserver?.disconnect();
  window.removeEventListener("resize", updateTabsScrollState);
});
</script>

<style scoped>
.carousel__item {
  -webkit-transition: background-color 300ms ease;
  -ms-transition: background-color 300ms ease;
  transition: background-color 300ms ease;
}
</style>
